import React, {useEffect, useState, createContext, useRef, ReactNode} from 'react';
import PropTypes from 'prop-types';
import {Props as ButtonProps} from 'src/components/button';

export interface OnboardingContextType {
  isLoading: boolean;
  isControlledStep: boolean;
  setLoading: (isLoading: boolean) => void;
  secondaryButton: {
    disabled: boolean;
    text: string;
  };
  primaryButton: {
    controlled: boolean;
    text: string;
    disabled: boolean;
    color?: ButtonProps['color'];
  };
  updatePrimaryButton: (props: Partial<OnboardingContextType['primaryButton']>) => void;
  updateSecondaryButton: (props: Partial<OnboardingContextType['secondaryButton']>) => void;
  resetPrimaryButton: () => void;
  setCanSubmit: (canSubmit: boolean) => void;
  canSubmit: boolean;
  skipStep: () => void;
}

interface Props {
  isControlledStep: boolean;
  children: ReactNode;
  skipStep: () => void;
  step: string;
}

export const OnboardingContext = createContext<OnboardingContextType>({} as OnboardingContextType);
OnboardingContext.displayName = 'OnboardingContext';

const defaultPrimaryButtonProps = {
  text: 'Next',
  disabled: false,
  controlled: false,
  loading: false,
};
const defaultSecondaryButtonProps = {text: 'Back', disabled: false};

export function OnboardingProvider(props: Props) {
  const [primaryButton, setPrimaryButtonProps] =
    useState<OnboardingContextType['primaryButton']>(defaultPrimaryButtonProps);
  const [secondaryButton, setSecondaryButtonProps] = useState<
    OnboardingContextType['secondaryButton']
  >(defaultSecondaryButtonProps);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const prevStep = useRef(props.step);

  const updatePrimaryButton: OnboardingContextType['updatePrimaryButton'] = (newProps) =>
    setPrimaryButtonProps({...defaultPrimaryButtonProps, ...newProps});
  const updateSecondaryButton: OnboardingContextType['updateSecondaryButton'] = (newProps) =>
    setSecondaryButtonProps({...defaultSecondaryButtonProps, ...newProps});
  const resetPrimaryButton = (): void => setPrimaryButtonProps(defaultPrimaryButtonProps);

  useEffect(() => {
    if (prevStep.current === props.step) return;
    resetPrimaryButton();
    prevStep.current = props.step;
  }, [props.step]);

  return (
    <OnboardingContext.Provider
      value={{
        isLoading,
        canSubmit,
        setCanSubmit,
        primaryButton,
        secondaryButton,
        updatePrimaryButton,
        updateSecondaryButton,
        resetPrimaryButton,
        setLoading,
        skipStep: props.skipStep,
        isControlledStep: props.isControlledStep,
      }}
    >
      {props.children}
    </OnboardingContext.Provider>
  );
}

OnboardingProvider.propTypes = {
  children: PropTypes.node,
};
